import * as THREE from "three";
import ThreeGlobe from "three-globe";
import Experience from "../Experience";

// Define a type for our pin data for better TypeScript support
import gsap from "gsap";

// Define a type for our pin data for better TypeScript support
interface PinData {
  id: string;
  lat: number;
  lng: number;
  text: string;
  size: number;
  color: string;
  type: 'cluster' | 'image';
  count?: number;
  path?: string;
  description?: string;
  user_name?: string;
}

const markerSvg = `<svg viewBox="-4 0 36 36">
      <path fill="currentColor" d="M14,0 C21.732,0 28,5.641 28,12.6 C28,23.963 14,36 14,36 C14,36 0,24.064 0,12.6 C0,5.641 6.268,0 14,0 Z"></path>
      <circle fill="black" cx="14" cy="14" r="7"></circle>
    </svg>`;

export default class Earth {
  public globe?: ThreeGlobe;
  private experience: Experience;
  private modalContainer: HTMLElement | null = null;
  private currentAnimation: gsap.core.Tween | null = null;
  
  // Dynamic fetching properties
  private currentTile = { x: 0, y: 0, z: 0 };
  private tileCache = new Map<string, PinData[]>();
  private knownPins = new Map<string, PinData>();
  private lastFetchTime = 0;
  private fetchThrottle = 500; // ms

  constructor() {
    this.experience = new Experience();
    this.createModal();
    this.setupInteractions();
    
    // Stop animation on user interaction
    this.experience.camera.controls.addEventListener('start', () => {
      if (this.currentAnimation) {
        this.currentAnimation.kill();
        this.currentAnimation = null;
      }
    });
  }

  update() {
    if (!this.globe) return;

    // Calculate current zoom level
    const GLOBE_RADIUS = 100;
    const currentDistance = this.experience.camera.instance.position.distanceTo(new THREE.Vector3(0,0,0));
    const currentAltitude = (currentDistance / GLOBE_RADIUS) - 1;
    const K = 2.0;
    let zoom = Math.floor(Math.log2(K / currentAltitude));
    if (zoom < 0) zoom = 0;
    if (zoom > 14) zoom = 14;

    // Calculate center lat/lng
    const { lat, lng } = this.globe.toGeoCoords(this.experience.camera.instance.position);
    
    // Convert to tile coordinates
    const n = Math.pow(2, zoom);
    const x = Math.floor((lng + 180) / 360 * n);
    const latRad = lat * Math.PI / 180;
    const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);

    // Check if tile changed
    if (this.currentTile.x !== x || this.currentTile.y !== y || this.currentTile.z !== zoom) {
      this.currentTile = { x, y, z: zoom };
      this.throttledFetch(zoom, x, y);
    }
    
    // Update display if zoom threshold crossed (Cluster <-> Image)
    // We can do this check more efficiently, but for now let's just ensure display is correct
    // Actually, calling it every frame is expensive if it rebuilds the globe data.
    // ThreeGlobe's htmlElementsData is relatively cheap if data hasn't changed, but we are filtering.
    // Let's rely on fetchPins to update display, AND if we switch modes.
    // We can track "lastDisplayMode" ('cluster' or 'image')
  }

  throttledFetch(z: number, x: number, y: number) {
    const now = Date.now();
    if (now - this.lastFetchTime > this.fetchThrottle) {
      // Load 3x3 grid of tiles
      const n = Math.pow(2, z);
      
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          // Wrap X (longitude)
          let neighborX = (x + dx) % n;
          if (neighborX < 0) neighborX += n;
          
          // Clamp Y (latitude) - don't wrap poles
          const neighborY = y + dy;
          if (neighborY >= 0 && neighborY < n) {
            this.fetchPins(z, neighborX, neighborY);
          }
        }
      }
      
      this.lastFetchTime = now;
    }
  }

  async fetchPins(z: number, x: number, y: number) {
    const key = `${z}/${x}/${y}`;
    
    // Even if in cache, we might want to ensure they are in knownPins (in case we cleared it?)
    // But for now, let's assume if it's in tileCache, we've processed it.
    if (this.tileCache.has(key)) {
      // Just update display to ensure correct filtering
      this.updatePinsDisplay();
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/images/tiles/${z}/${x}/${y}`);
      if (!response.ok) throw new Error('Failed to fetch pins');
      
      const data = await response.json();
      let pins: PinData[] = [];

      if (data.type === 'cluster') {
        pins = data.features.map((f: any) => ({
          id: f.id,
          lat: f.lat,
          lng: f.lon,
          text: `${f.count} images`,
          size: 30 + Math.min(f.count, 20),
          color: "#fde047",
          type: 'cluster',
          count: f.count
        }));
      } else if (data.type === 'image') {
         pins = data.features.map((f: any) => ({
          id: f.id, // Use DB ID
          lat: f.latitude,
          lng: f.longitude,
          text: f.description || 'Image',
          size: 30,
          color: "#ef4444",
          type: 'image',
          path: f.path,
          description: f.description,
          user_name: f.user_name
        }));
      }

      this.tileCache.set(key, pins);
      
      // Merge into knownPins
      pins.forEach(p => this.knownPins.set(p.id, p));
      
      this.updatePinsDisplay();
      
    } catch (error) {
      console.error('Error loading pins:', error);
    }
  }

  updatePinsDisplay() {
    if (!this.globe) return;

    // Determine mode based on current zoom
    const isImageMode = this.currentTile.z >= 8;
    const targetType = isImageMode ? 'image' : 'cluster';

    // Filter known pins by type - clusters stay in knownPins but are hidden in image mode
    const visiblePins = Array.from(this.knownPins.values()).filter(p => p.type === targetType);
    
    this.globe.htmlElementsData(visiblePins);
  }

  createModal() {
    this.modalContainer = document.createElement('div');
    this.modalContainer.id = 'image-modal';
    this.modalContainer.className = 'modal-hidden';
    
    // Structure: Overlay -> Content (Background) -> Image + Letter-style text + Close
    this.modalContainer.innerHTML = `
      <div class="modal-overlay"></div>
      <div class="modal-content">
        <button class="modal-close">&times;</button>
        <div class="modal-body">
          <img class="modal-image" src="" alt="Pin Image" />
          <div class="modal-letter">
            <p class="modal-username"></p>
            <p class="modal-description"></p>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.modalContainer);

    // Event listeners
    const closeBtn = this.modalContainer.querySelector('.modal-close');
    const overlay = this.modalContainer.querySelector('.modal-overlay');
    
    const close = () => this.closeModal();
    closeBtn?.addEventListener('click', close);
    overlay?.addEventListener('click', close);
  }

  openModal(imageUrl: string, username: string, description: string) {
    if (!this.modalContainer) return;
    
    const img = this.modalContainer.querySelector('.modal-image') as HTMLImageElement;
    const usernameEl = this.modalContainer.querySelector('.modal-username') as HTMLElement;
    const desc = this.modalContainer.querySelector('.modal-description') as HTMLElement;
    
    if (img) img.src = imageUrl;
    if (usernameEl) usernameEl.textContent = `${username}:` || 'Anonymous:';
    if (desc) desc.textContent = description || '';
    
    this.modalContainer.classList.remove('modal-hidden');
    this.modalContainer.classList.add('modal-visible');
  }

  closeModal() {
    if (!this.modalContainer) return;
    this.modalContainer.classList.remove('modal-visible');
    this.modalContainer.classList.add('modal-hidden');
  }

  load(onReady: () => void) {
    this.globe = new ThreeGlobe()
      // .globeTileEngineUrl(
      //   (x, y, l) => `https://tile.openstreetmap.org/${l}/${x}/${y}.png`
      // )
      .globeTileEngineUrl(
        (x, y, l) => `https://mt1.google.com/vt/lyrs=y&x=${x}&y=${y}&z=${l}`
      )
      // .globeImageUrl("./textures/earth-dark.jpg")
      // .bumpImageUrl("./textures/earth-topology.png")

      // .globeTileEngineUrl(
      //   (x, y, l) =>
      //     `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${l}/${y}/${x}.jpg`
      // )
      .atmosphereColor("#3a97ff")
      .atmosphereAltitude(0.2)
      .htmlElement((d) => {
        const el = document.createElement("div");
        el.innerHTML = markerSvg;
        el.style.color = (d as any).color;
        el.style.width = `${(d as any).size}px`;
        el.style.transition = "opacity 250ms";
        el.style.cursor = "pointer";
        el.style.pointerEvents = "auto";
        
        // Add tooltip or text
        if ((d as any).text) {
          el.title = (d as any).text;
        }

        el.onclick = () => this.handlePinClick(d as PinData);
        
        return el;
      })
      .htmlElementVisibilityModifier(
        (el, isVisible) => (el.style.opacity = isVisible ? "1" : "0")
      )
      .onGlobeReady(onReady);
    this.globe.globeTileEngineMaxLevel(14);
    this.experience.scene.add(this.globe);
    
    // Initial load
    this.fetchPins(0, 0, 0);
  }

  handlePinClick(data: PinData) {
    if (data.type === 'cluster') {
      // Rotate to center the pin
      console.log("Rotating to cluster at", data.lat, data.lng);

      // Use getCoords to convert lat/lng to 3D position
      if (!this.globe) return;

      const GLOBE_RADIUS = 100;
      const currentDistance = this.experience.camera.instance.position.distanceTo(new THREE.Vector3(0,0,0));
      const currentAltitude = (currentDistance / GLOBE_RADIUS) - 1;
      
      // Estimate current zoom level
      // Formula: zoom = log2(K / altitude)
      // Assuming K=2.0 (Altitude 2.0 ~= Zoom 0)
      const K = 2.0;
      let currentZoom = Math.log2(K / currentAltitude);
      if (currentZoom < 0) currentZoom = 0;
      
      const MAX_ZOOM = 14;
      // Target zoom is half the difference to max
      const targetZoom = currentZoom + (MAX_ZOOM - currentZoom) / 2;
      
      // Convert back to altitude
      const targetAltitude = K / Math.pow(2, targetZoom);
      const targetDistance = (targetAltitude + 1) * GLOBE_RADIUS;
      
      console.log(`Zooming from ${currentZoom.toFixed(2)} to ${targetZoom.toFixed(2)} (Dist: ${currentDistance.toFixed(0)} -> ${targetDistance.toFixed(0)})`);

      // Get vector for the lat/lng on the surface
      const { x, y, z } = this.globe.getCoords(data.lat, data.lng, 0);
      
      // Create a vector and normalize it to get direction, then multiply by TARGET distance
      const targetVec = new THREE.Vector3(x, y, z).normalize().multiplyScalar(targetDistance);
      
      // Kill any existing animation
      if (this.currentAnimation) {
        this.currentAnimation.kill();
      }

      this.currentAnimation = gsap.to(this.experience.camera.instance.position, {
        duration: 1.5,
        x: targetVec.x,
        y: targetVec.y,
        z: targetVec.z,
        onUpdate: () => {
          this.experience.camera.controls.update();
        },
        onComplete: () => {
          this.currentAnimation = null;
        }
      });
      

    } else if (data.type === 'image') {
      // Open modal
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      // Construct full image URL
      // data.path is like "username/filename"
      const fullUrl = `${apiUrl}/images/${data.path}`;
      this.openModal(fullUrl, (data as any).user_name || 'Anonymous', data.description || '');
    }
  }

  /**
   * Align camera to specific coordinates with optional zooming
   */
  alignToCoordinates(lat: number, lng: number, targetZoom?: number) {
    if (!this.globe) return;
    
    const GLOBE_RADIUS = 100;
    let targetDistance;

    if (targetZoom !== undefined) {
      // Calculate distance for target zoom
      const K = 2.0;
      const targetAltitude = K / Math.pow(2, targetZoom);
      targetDistance = (targetAltitude + 1) * GLOBE_RADIUS;
    } else {
      // Keep current distance
      targetDistance = this.experience.camera.instance.position.distanceTo(new THREE.Vector3(0, 0, 0));
    }
    
    // Get vector for the lat/lng on the surface
    const { x, y, z } = this.globe.getCoords(lat, lng, 0);
    
    // Create a vector and normalize it to get direction, then multiply by target distance
    const targetVec = new THREE.Vector3(x, y, z).normalize().multiplyScalar(targetDistance);
    
    // Kill any existing animation
    if (this.currentAnimation) {
      this.currentAnimation.kill();
    }

    this.currentAnimation = gsap.to(this.experience.camera.instance.position, {
      duration: 2,
      x: targetVec.x,
      y: targetVec.y,
      z: targetVec.z,
      ease: "power2.inOut",
      onUpdate: () => {
        this.experience.camera.controls.update();
      },
      onComplete: () => {
        this.currentAnimation = null;
      }
    });
  }

  setupInteractions() {
    // Double click to zoom
    this.experience.canvas.addEventListener('dblclick', (event) => {
      if (!this.globe) return;

      // Calculate mouse position in normalized device coordinates
      const rect = this.experience.canvas.getBoundingClientRect();
      const mouse = new THREE.Vector2();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Raycast
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, this.experience.camera.instance);

      // Intersect with globe
      const intersects = raycaster.intersectObject(this.globe, true);

      if (intersects.length > 0) {
        // Find the first intersection with the globe mesh
        const hit = intersects[0];
        
        // Convert hit point to lat/lng
        const { lat, lng } = this.globe.toGeoCoords(hit.point);
        
        // Calculate current zoom and target zoom (+2 levels)
        const GLOBE_RADIUS = 100;
        const currentDistance = this.experience.camera.instance.position.distanceTo(new THREE.Vector3(0,0,0));
        const currentAltitude = (currentDistance / GLOBE_RADIUS) - 1;
        const K = 2.0;
        let currentZoom = Math.log2(K / currentAltitude);
        
        // Clamp target zoom
        const targetZoom = Math.min(currentZoom + 2, 14);
        
        console.log(`Double click at ${lat.toFixed(2)}, ${lng.toFixed(2)} - Zooming to ${targetZoom.toFixed(2)}`);
        this.alignToCoordinates(lat, lng, targetZoom);
      }
    });
  }

  /**
   * Refresh data and show a specific image by ID
   */
  async refreshAndShowImage(imageId: string) {
    // Clear cache to force refetch
    this.tileCache.clear();
    
    // Refetch current tile
    await this.fetchPins(this.currentTile.z, this.currentTile.x, this.currentTile.y);
    
    // Find the image in knownPins
    const imagePin = this.knownPins.get(imageId);
    if (imagePin && imagePin.type === 'image') {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const fullUrl = `${apiUrl}/images/${imagePin.path}`;
      this.openModal(fullUrl, (imagePin as any).user_name || 'Anonymous', imagePin.description || '');
    }
  }

  dispose() {
    if (this.globe) {
      // Dispose three-globe resources
      this.globe.labelsData([]); // Clear the labels

      // A more thorough dispose (if you were removing the globe entirely):
      const cleanMaterial = (material: THREE.Material | THREE.Material[]) => {
        if (Array.isArray(material)) {
          material.forEach(cleanMaterial);
        } else {
          material.dispose();
        }
      };

      const cleanObject = (object: THREE.Object3D) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          cleanMaterial(object.material);
        }
      };

      this.globe.traverse(cleanObject);
      this.experience.scene.remove(this.globe);
    }
    
    if (this.modalContainer) {
      document.body.removeChild(this.modalContainer);
      this.modalContainer = null;
    }
  }

  async loadData() {
    // Deprecated in favor of fetchPins
    this.fetchPins(0, 0, 0);
  }
}
